import React from 'react';
import RegistrationPlate from '../common/RegistrationPlate';
import { WaitingMark, ActionRequiredMark } from './icons';
import { formatSekAmount } from '../../lib/format.jsx';
import { formatRemaining, useCountdown } from '../../lib/dealClock';
import './ActiveDealPanel.css';

// The Overview's "ongoing deal" composition — ported from Klargo-Reference's
// DashboardActiveDealPanel / dashboardPresentation: one structured surface that
// says what the deal is, where it stands and the one thing to do next. It is
// drawn from the user's REAL open deal (see ChooseAction.jsx); the action only
// resumes the existing Buy/Sell flow, nothing of the flow is re-implemented here.

// Stage of the user's own side of the deal, from the contract flags. step = position on the real
// flow track (SellerFlowTrack, DEAL_STEP_TOTAL milestones): 1 vehicle type, 2 registration,
// 3 connection, 4 contract, 5 payment, 6 change of ownership.
// The deal pages' own rail: vehicle, registration, connection, contract, payment, ownership.
export const DEAL_STEP_TOTAL = 6;

export function resolveDealStage(deal, userId) {
  const isSeller = deal.sellerUserId === userId;
  // Signed by both: the deal is past its signing, at its payment stage - or, once the seller has
  // confirmed the money, at its change of ownership - whichever side is looking. The server says
  // which (ContractPayment/GetDealAwaitingPayment); a deal it did not describe is at payment.
  if (deal.dealComplete && !deal.dealCancel) {
    return deal.awaitingStage === 'ownership'
      ? { stage: 'ownership', isSeller, step: 6 }
      : { stage: 'payment', isSeller, step: 5 };
  }
  if (isSeller) {
    if (deal.sellerDealComplete) return { stage: 'contract_ready', isSeller, step: 4 };
    if (deal.buyerDealConnected) return { stage: 'buyer_joined', isSeller, step: 3 };
    return { stage: 'waiting_for_buyer', isSeller, step: 3 };
  }
  if (deal.sellerDealComplete) return { stage: 'contract_ready', isSeller, step: 4 };
  return { stage: 'waiting_for_seller', isSeller, step: 3 };
}

const STAGE_TEXT = {
  waiting_for_buyer: { tone: 'waiting', status: 'statusWaitingForBuyer', support: 'supportWaitingForBuyer', action: 'actionView' },
  buyer_joined: { tone: 'action', status: 'statusBuyerJoined', support: 'supportBuyerJoined', action: 'actionView' },
  waiting_for_seller: { tone: 'waiting', status: 'statusWaitingForSeller', support: 'supportWaitingForSeller', action: 'actionView' },
  contract_ready: { tone: 'action', status: 'statusContractReady', support: null, action: 'actionOpenContract' },
};

const TONE_COLOUR = { waiting: 'var(--ds-slate-fg)', action: 'var(--ds-amber-fg)' };
const TONE_MARK = { waiting: WaitingMark, action: ActionRequiredMark };

// "Time remaining" counts down from the server's own reading (see lib/dealClock), so it never
// depends on the browser's clock or time zone. The same helpers drive the header countdown on the
// deal pages, so the two can never disagree.

// Wall-clock moment shown to the user.
const formatMoment = (ms, language) =>
  new Date(ms).toLocaleString(language && language.startsWith('sv') ? 'sv-SE' : 'en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });

const BandFact = ({ label, value, tabular }) => (
  <div className="adp-fact">
    <div className="adp-fact-label">{label}</div>
    <div className={`adp-fact-value${tabular ? ' adp-fact-value--num' : ''}`}>{value}</div>
  </div>
);

export default function ActiveDealPanel({ t, language, deal, userId, onResume, onViewVehicle }) {
  const { stage, isSeller, step } = resolveDealStage(deal, userId);
  // The inactivity window runs from the car search until the SELLER CONFIRMS THE PAYMENT, so a
  // signed but unpaid deal is still running and still shows its countdown. Only a deal the server
  // no longer reports as running — finished, cancelled or already expired — has no clock.
  const dealState = deal.dealState || (deal.dealCancel ? 'cancelled' : 'in_progress');
  const closed = dealState !== 'in_progress';
  const remainingSource = closed ? null : deal.dealRemainingSeconds;
  const remaining = useCountdown(remainingSource, deal.fetchedAt);
  const expiresAtMs = Number.isFinite(remainingSource)
    ? (deal.fetchedAt || 0) + remainingSource * 1000
    : null;
  // When the deal was created, from the server's own record of it. It must NOT be derived from the
  // expiry: the server's creation time is the record of when the deal started, whatever the
  // deadline arithmetic is. (DealExpiryTime is the start instant and is fixed; it no longer slides.)
  const createdAt = deal.creationTime ? new Date(deal.creationTime).getTime() : NaN;
  const createdAtMs = Number.isFinite(createdAt) ? createdAt : null;
  // The payment stage reads differently from each side: the buyer pays, the seller confirms.
  // The seller at the contract step still sees who joined and the confirmation line.
  const text = stage === 'payment'
    ? {
        tone: 'action',
        status: isSeller ? 'statusPaymentSeller' : 'statusPaymentBuyer',
        support: isSeller ? 'supportPaymentSeller' : 'supportPaymentBuyer',
        action: 'actionView',
      }
    // The money has arrived. The seller reports the transfer, so the task is theirs; the buyer
    // has nothing to do but wait for it.
    : stage === 'ownership'
    ? {
        tone: isSeller ? 'action' : 'waiting',
        status: isSeller ? 'statusOwnershipSeller' : 'statusOwnershipBuyer',
        support: isSeller ? 'supportOwnershipSeller' : 'supportOwnershipBuyer',
        action: 'actionView',
      }
    : isSeller && stage === 'contract_ready'
      ? { ...STAGE_TEXT.buyer_joined, tone: 'action', action: 'actionView' }
      : STAGE_TEXT[stage];
  const total = DEAL_STEP_TOTAL;
  const counterpart = isSeller ? deal.buyerName : deal.sellerName;
  const key = (k) => t(`chooseAction.active.${k}`);
  const vehicle = [deal.carInfoVehicleDesignation, deal.carInfoTradeName].filter(Boolean).join(' ');
  const phase = key(
    stage === 'ownership' ? 'phaseOwnership'
      : stage === 'payment' ? 'phasePayment'
      : stage === 'contract_ready' ? 'phaseContract'
      : 'phaseConnection',
  );
  const status = key(text.status).replace('{name}', counterpart || '');
  const Mark = TONE_MARK[text.tone];
  const roleColour = isSeller ? 'var(--ds-seller-fg)' : 'var(--ds-buyer-fg)';

  return (
    <section aria-label={key('section')} className="adp" data-testid="home-active-deal">
      <h1 className="ds-title-route m-0" data-testid="home-h1">
        {isSeller ? key('titleSale') : key('titlePurchase')}
      </h1>
      <div className="adp-role" style={{ color: roleColour }} data-testid="deal-role">
        <span aria-hidden="true" className="adp-dot" style={{ background: isSeller ? 'var(--ds-seller)' : 'var(--ds-buyer)' }} />
        <span>{isSeller ? key('roleSelling') : key('roleBuying')}</span>
      </div>

      <div className="adp-band" data-testid="deal-summary-band">
        <div className="adp-band-row">
          <div className="adp-identity">
            <RegistrationPlate value={deal.carInfoRegistrationNo} size="object" />
            {vehicle ? (
              <div className="adp-vehicle">{vehicle}</div>
            ) : (
              <div className="adp-pending">{`${key('car')} · ${key('identityPending')}`}</div>
            )}
          </div>
          <div className={`adp-facts${expiresAtMs !== null ? " adp-facts--timed" : ""}`}>
            {counterpart ? <BandFact label={isSeller ? key('buyer') : key('seller')} value={counterpart} /> : null}
            {createdAtMs !== null ? <BandFact label={key('created')} value={formatMoment(createdAtMs, language)} tabular /> : null}
            {expiresAtMs !== null ? <BandFact label={key('expires')} value={formatMoment(expiresAtMs, language)} tabular /> : null}
            {remaining !== null ? (
              <div className="adp-fact" data-testid="deal-remaining">
                <div className="adp-fact-label">{key('timeRemaining')}</div>
                <div className={`adp-fact-value adp-fact-value--num${remaining <= 300 ? ' adp-fact-value--urgent' : ''}`} role="timer" aria-label={key('timeRemaining')}>
                  {formatRemaining(remaining)}
                </div>
              </div>
            ) : null}
            <BandFact label={key('price')} value={formatSekAmount(deal.carValuationBySeller || 0, language)} tabular />
          </div>
        </div>
      </div>

      <div className="adp-state-wrap">
        <div role="status" data-testid="deal-state">
          <div className="adp-state-head">
            <div className="adp-pill" style={{ color: TONE_COLOUR[text.tone] }}>
              <Mark width={16} height={16} />
              <span>{key(text.tone === 'action' ? 'pillAction' : 'pillWaiting')}</span>
            </div>
            <div className="adp-phase" data-testid="deal-phase">
              {key('step').replace('{current}', String(step)).replace('{total}', String(total))} · {phase}
            </div>
          </div>
          <h2 className="ds-title-component adp-status">{status}</h2>
          {text.support ? <p className="adp-support">{key(text.support)}</p> : null}
        </div>

        <div className="adp-cta">
          <button
            type="button"
            onClick={onResume}
            data-testid="home-deal-cta"
            className="ds-action-ink"
          >
            {key(text.action)}
          </button>
        </div>
      </div>

      {isSeller && onViewVehicle ? (
        <div className="adp-vehicle-link">
          <button type="button" onClick={onViewVehicle} data-testid="deal-vehicle-link" className="ds-action-text">
            <span className="ds-underline">{key('viewVehicle')}</span>
          </button>
        </div>
      ) : null}

      <p className="adp-note">{key('oneAtATime')}</p>
    </section>
  );
}
